import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import PatientSummaryCard, { PatientSummary } from "@/components/pilates/PatientSummaryCard";
import { Loader2 } from "lucide-react";

interface PatientSummaryWrapperProps {
  patientId: string;
}

interface AIInsight {
  text?: string;
  message?: string;
}

interface AlertMessage {
  message?: string;
}

export function PatientSummaryWrapper({ patientId }: PatientSummaryWrapperProps) {
  const [patientData, setPatientData] = useState<PatientSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPatientData();
  }, [patientId]);

  const loadPatientData = async () => {
    setIsLoading(true);
    try {
      // Buscar dados básicos do paciente
      const { data: patient, error: patientError } = await supabase
        .from('patients_onboarding')
        .select('*')
        .eq('id', patientId)
        .single();

      if (patientError) throw patientError;

      // ✅ REMOVIDO: patient_health_records não é mais usado
      const lastRecord = null;

      // Buscar alertas recentes
      const { data: alerts } = await supabase
        .from('alerts_onboarding')
        .select('message')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })
        .limit(3);

      // Buscar insights de IA
      const { data: insights } = await supabase
        .from('ai_global_analysis_cache')
        .select('json_result')
        .eq('patient_id', patientId)
        .order('generated_at', { ascending: false })
        .limit(1)
        .single();

      const jsonResult = insights?.json_result as { highlights?: AIInsight[] } | null;
      const aiRecommendations = jsonResult?.highlights
        ?.slice(0, 3)
        .map((h: AIInsight | string) => {
          if (typeof h === 'string') return h;
          if (typeof h === 'object' && h.text) return h.text;
          if (typeof h === 'object' && h.message) return h.message;
          return '';
        })
        .filter((text: string | undefined): text is string => !!text && text.length > 0) || [];

      setPatientData({
        id: patient.id,
        full_name: patient.full_name,
        avatar_url: patient.avatar_url,
        data_nascimento: patient.data_nascimento,
        sexo: patient.sexo,
        lastRecord: lastRecord ? {
          title: lastRecord.title,
          date: lastRecord.created_at,
        } : undefined,
        alerts: alerts?.map(a => {
          if (typeof a.message === 'string') return a.message;
          const msgObj = a.message as AlertMessage;
          if (msgObj && typeof msgObj === 'object' && msgObj.message) return msgObj.message;
          return '';
        }).filter((msg): msg is string => msg.length > 0) || [],
        aiRecommendations,
      });
    } catch (error) {
      console.error('Erro ao carregar dados do paciente:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <PatientSummaryCard patient={patientData} />;
}
